import { useEffect, useState, useRef } from 'react';
import { message } from 'antd';
import { getExpertExamRecord, getFormSignature, updateSignatureState } from '../../../services/exam';
import { getAsset, getDiagnostics } from '../../../services/general';
import { filterAndSortExpertFields } from '../../ExamPage/components/signature/signatureUtils';
import { consultationService } from '../../../services/medicalConsultation';

export const useConsultationDiagnosticExam = ({ record, open, onExamSaved, form, showDiagnosticTypification }) => {
  const [documentUrl, setDocumentUrl] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [examRecord, setExamRecord] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [fetchingData, setFetchingData] = useState(false);
  const examSaveHandlersRef = useRef({});
  const expertFieldValuesRef = useRef({});
  const selectedStateRef = useRef(null);
  const hasUnsavedDefaultDiagnosticsRef = useRef(false);

  const admissionPatientExamId = record?.admission_patient_exam;

  useEffect(() => {
    const fetchData = async () => {
      setFetchingData(true);
      try {
        let expertRecord = null;
        let viewableDocumentId = null;
        let formSignatureData = null;

        if (!record?.isGroup && admissionPatientExamId) {
          expertRecord = await getExpertExamRecord(admissionPatientExamId);
          setExamRecord(expertRecord);

          if (expertRecord?.viewableFiles?.length > 0) {
            viewableDocumentId = expertRecord.viewableFiles[0].documentResultId;
          } else if (expertRecord?.documentResultId) {
            viewableDocumentId = expertRecord.documentResultId;
          }

          if (record?.need_expert_sign) {
            formSignatureData = await getFormSignature({ admissionPatientExamId });
            setSignatureData(formSignatureData);
          }

          const examDiagnosticsList = form?.getFieldValue('examDiagnostics') || [];
          const admissionPatientExamIndexOnList = examDiagnosticsList.findIndex(
            (exam) => String(exam.examId) === String(admissionPatientExamId)
          );
          const existingExamDiagnostics = admissionPatientExamIndexOnList >= 0 ? examDiagnosticsList[admissionPatientExamIndexOnList] : null;

          const hasNoDiagnosticsYet =
            !existingExamDiagnostics ||
            !existingExamDiagnostics.diagnostics ||
            existingExamDiagnostics.diagnostics.length === 0;

          let isUnsavedExam = false;
          if (record?.need_expert_sign) {
            isUnsavedExam = formSignatureData?.status === 'created';
          } else if (record?.allow_diagnostics) {
            const admissionPatientExamState = expertRecord?.admission_patient_exam_state;
            isUnsavedExam = (admissionPatientExamState === 'created' || admissionPatientExamState === 'occupied') && hasNoDiagnosticsYet;
          }

          const defaultDiagnosticCodes = record?.default_diagnostics;

          if (
            isUnsavedExam &&
            record?.to_doctor &&
            admissionPatientExamIndexOnList >= 0 &&
            Array.isArray(defaultDiagnosticCodes) &&
            defaultDiagnosticCodes.length > 0
          ) {
            try {
              const fetchedDefaultDiagnostics = await getDiagnostics({
                codes: [...new Set(defaultDiagnosticCodes)].join(','),
              });

              const formattedDefaults = fetchedDefaultDiagnostics.map(diagnostic => ({
                diagnostic_id: diagnostic.id,
                code: diagnostic.code,
                description: diagnostic.description,
                diagnosticable_id: admissionPatientExamId,
                diagnosticable_type: 'AdmissionPatientExam',
                indication: diagnostic.recommendations || diagnostic.indications || diagnostic.indication,
                suggestion: diagnostic.restrictions || diagnostic.suggestions || diagnostic.suggestion,
                pathological: false,
                classification: 'presumptive',
                occupational: 'other',
              }));

              if (formattedDefaults.length > 0) {
                form.setFieldsValue({
                  examDiagnostics: examDiagnosticsList.map((exam, idx) =>
                    idx === admissionPatientExamIndexOnList ? { ...exam, diagnostics: formattedDefaults } : exam
                  ),
                });
                hasUnsavedDefaultDiagnosticsRef.current = true;
              }
            } catch (error) {
              console.error('Error fetching default diagnostics:', error);
            }
          }
        }

        if (viewableDocumentId) {
          setDocumentLoading(true);
          const response = await getAsset({
            field: 'document',
            id: viewableDocumentId,
            type: 'AdmissionProcedureResult',
          });
          setDocumentUrl(response?.document_url || null);
        } else {
          setDocumentUrl(null);
        }
      } catch (error) {
        console.error('Error on loading exam data:', error);
        message.error({
          content: `Error al cargar información del examen.`,
          style: { marginTop: '40vh' }
        });
        setDocumentUrl(null);
      } finally {
        setDocumentLoading(false);
        setFetchingData(false);
      }
    };

    if (open && record) {
      hasUnsavedDefaultDiagnosticsRef.current = false;
      fetchData();
    } else {
      if (hasUnsavedDefaultDiagnosticsRef.current && form && admissionPatientExamId) {
        const examDiagnosticsList = form.getFieldValue('examDiagnostics') || [];
        const admissionPatientExamIndexOnList = examDiagnosticsList.findIndex(
          (exam) => String(exam.examId) === String(admissionPatientExamId)
        );
        if (admissionPatientExamIndexOnList >= 0) {
          form.setFieldsValue({
            examDiagnostics: examDiagnosticsList.map((exam, idx) =>
              idx === admissionPatientExamIndexOnList ? { ...exam, diagnostics: [] } : exam
            ),
          });
        }
      }
      hasUnsavedDefaultDiagnosticsRef.current = false;

      setDocumentUrl(null);
      setExamRecord(null);
      setSignatureData(null);
    }
  }, [open, record, admissionPatientExamId, form]);

  const handleSaveAllExams = async () => {
    setSaving(true);
    try {
      const promises = Object.values(examSaveHandlersRef.current).map(saveExamTabHandler => saveExamTabHandler?.());
      await Promise.all(promises);
      if (record?.to_doctor && record?.need_expert_sign && admissionPatientExamId && signatureData?.expert_fields) {
        const expertFieldValues = expertFieldValuesRef.current || {};
        const selectedState = selectedStateRef.current;
        if (stateField && !selectedState && !stateField.result) {
          message.error({
            content: 'Debe seleccionar un estado antes de guardar.',
            style: { marginTop: '40vh' },
          });
          return;
        }

        let updatedExpertFields = (signatureData.expert_fields || []).map(field => {
          const formValue = expertFieldValues[field.id];
          const hasValue = formValue !== undefined && formValue !== '' && formValue !== null;
          return { ...field, result: hasValue ? formValue : field.result };
        });
        if (stateField && selectedState) {
          const stateIdx = updatedExpertFields.findIndex(expertField => expertField.id === stateField.id);
          if (stateIdx >= 0) {
            updatedExpertFields[stateIdx] = { ...updatedExpertFields[stateIdx], result: selectedState };
          } else {
            updatedExpertFields.push({ ...stateField, result: selectedState });
          }
        }

        const examData = { admissionPatientExamId, body: { expertFields: updatedExpertFields } };
        await updateSignatureState({ ...examData, type: 'saved' });
        await updateSignatureState({ ...examData, type: 'signed' });
      }

      const supportsDiagnostics = record?.isGroup || record?.need_expert_sign || record?.allow_diagnostics;
      if (form && supportsDiagnostics) {
        const isSpecialty = !!record?.isGroup;
        const formValues = form.getFieldsValue();
        const arrayName = isSpecialty ? 'specialtyDiagnostics' : 'examDiagnostics';
        const entities = formValues[arrayName] || [];

        let entityData;
        let entityId;

        if (isSpecialty) {
          entityData = entities.filter(Boolean).find(entity => entity.specialtyName && entity.specialtyName.toLowerCase().includes('laboratorio')) || entities.filter(Boolean)[0];
          entityId = entityData ? entityData.examSpecialtyId : null;
        } else {
          entityId = admissionPatientExamId;
          entityData = entities.filter(Boolean).find(entity => String(entity.examId) === String(entityId));
        }

        if (entityData && entityId) {
          const diagnostics = (entityData.diagnostics || [])
            .filter(diagnostic => diagnostic && (diagnostic.id || diagnostic.diagnostic_id))
            .map(diagnostic => {
              const diagnosticData = {
                id: diagnostic.id,
                diagnostic_id: diagnostic.diagnostic_id,
                diagnosticable_id: diagnostic.diagnosticable_id,
                diagnosticable_type: diagnostic.diagnosticable_type,
                indication: diagnostic.indication,
                suggestion: diagnostic.suggestion,
              };
              if (showDiagnosticTypification) {
                diagnosticData.pathological = diagnostic.pathological;
                diagnosticData.classification = diagnostic.classification;
                diagnosticData.occupational = diagnostic.occupational;
              }
              return diagnosticData;
            });

          await consultationService.saveEntityDiagnostics({
            entityType: isSpecialty ? 'specialty' : 'exam',
            entityId: entityId,
            medicalConsultationId: record?.medical_consultation_id,
            diagnostics: diagnostics
          });
        }
      }

      hasUnsavedDefaultDiagnosticsRef.current = false;

      if (onExamSaved) {
        onExamSaved();
      }

      message.success({
        content: 'Se ha guardado el examen exitosamente.',
        style: { marginTop: '40vh' },
      });
    } catch (error) {
      console.error('Error on saving:', error);
      message.error({
        content: 'Error al guardar los cambios',
        style: { marginTop: '40vh' },
      });
    } finally {
      setSaving(false);
    }
  };

  const stateField = signatureData?.expert_fields?.find(field => field.code?.startsWith('ESTADO_'));

  const otherExpertFields = filterAndSortExpertFields({
    expertFields: signatureData?.expert_fields,
    expertFieldGroups: signatureData?.extra_data?.expertFieldGroups,
    isDrawer: true
  });

  return {
    documentUrl,
    documentLoading,
    saving,
    examRecord,
    signatureData,
    fetchingData,
    examSaveHandlersRef,
    expertFieldValuesRef,
    selectedStateRef,
    handleSaveAllExams,
    stateField,
    otherExpertFields,
  };
};
